import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Upload, X, ImageOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Product } from '@/types';
import { useCreateProduct, useUpdateProduct } from '@/services/api/endpoints/ProductService';
import { useGetMachines } from '@/services/api/endpoints/MachineService';
import { uploadProductImage } from '@/services/api/endpoints/UploadService';

interface ProductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
}

const INITIAL_STATE = {
  machineId: '',
  slotNumber: '',
  name: '',
  price: '',
  discountedPrice: '',
  imageUrl: '',
};

export function ProductModal({ open, onOpenChange, product }: ProductModalProps) {
  const { t } = useTranslation();
  const isEdit = !!product;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const { data: machinesData } = useGetMachines(1, 100);
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const isPending = createMutation.isPending || updateMutation.isPending || uploading;

  useEffect(() => {
    if (product) {
      setFormData({
        machineId: product.machineId,
        slotNumber: String(product.slotNumber),
        name: product.name,
        price: String(product.price),
        discountedPrice: product.discountedPrice != null ? String(product.discountedPrice) : '',
        imageUrl: product.imageUrl || '',
      });
      setImagePreview(product.imageUrl || null);
    } else {
      setFormData(INITIAL_STATE);
      setImagePreview(null);
    }
    setImageFile(null);
    setErrors({});
  }, [product, open]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max file size is 5MB');
      return;
    }

    setImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData(prev => ({ ...prev, imageUrl: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('common.required');
    if (!formData.machineId) newErrors.machineId = t('common.required');
    if (!formData.slotNumber || isNaN(Number(formData.slotNumber))) newErrors.slotNumber = t('common.required');
    if (!formData.price || isNaN(Number(formData.price)) || Number(formData.price) < 0) newErrors.price = t('common.required');
    if (formData.discountedPrice.trim()) {
      const dp = Number(formData.discountedPrice);
      if (isNaN(dp) || dp <= 0) {
        newErrors.discountedPrice = t('products.discountInvalid') || 'Mora biti pozitivan broj';
      } else if (dp >= Number(formData.price)) {
        newErrors.discountedPrice = t('products.discountTooHigh') || 'Mora biti manje od redovne cene';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      let imageUrl = formData.imageUrl.trim() || undefined;

      // Upload image if a new file was selected
      if (imageFile) {
        setUploading(true);
        try {
          imageUrl = await uploadProductImage(
            imageFile,
            formData.machineId,
            Number(formData.slotNumber)
          );
        } catch (err: any) {
          toast.error(err?.response?.data?.message || 'Image upload failed');
          setUploading(false);
          return;
        }
        setUploading(false);
      }

      const payload = {
        machineId: formData.machineId,
        slotNumber: Number(formData.slotNumber),
        name: formData.name.trim(),
        price: Number(formData.price),
        discountedPrice: formData.discountedPrice.trim() ? Number(formData.discountedPrice) : null,
        imageUrl,
      };

      if (isEdit) {
        updateMutation.mutate(
          { id: product.id, ...payload },
          {
            onSuccess: () => {
              toast.success(t('products.updated'));
              onOpenChange(false);
            },
            onError: (error: any) => {
              toast.error(error?.response?.data?.message || t('common.error'));
            },
          }
        );
      } else {
        createMutation.mutate(payload, {
          onSuccess: () => {
            toast.success(t('products.created'));
            onOpenChange(false);
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.message || t('common.error'));
          },
        });
      }
    } catch {
      toast.error(t('common.error'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('products.edit') : t('products.create')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('products.name')} *</Label>
            <Input
              value={formData.name}
              onChange={e => handleChange('name', e.target.value)}
              className={errors.name ? 'border-destructive' : ''}
              disabled={isPending}
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label>{t('machines.title')} *</Label>
            <select
              value={formData.machineId}
              onChange={e => handleChange('machineId', e.target.value)}
              className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ${errors.machineId ? 'border-destructive' : 'border-input'}`}
              disabled={isPending || isEdit}
            >
              <option value="">{t('common.select')}</option>
              {machinesData?.items.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.code})
                </option>
              ))}
            </select>
            {errors.machineId && <p className="text-sm text-destructive">{errors.machineId}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('products.slotNumber')} *</Label>
              <Input
                type="number"
                min="1"
                value={formData.slotNumber}
                onChange={e => handleChange('slotNumber', e.target.value)}
                className={errors.slotNumber ? 'border-destructive' : ''}
                disabled={isPending}
              />
              {errors.slotNumber && <p className="text-sm text-destructive">{errors.slotNumber}</p>}
            </div>

            <div className="space-y-2">
              <Label>{t('products.price')} (RSD) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={e => handleChange('price', e.target.value)}
                className={errors.price ? 'border-destructive' : ''}
                disabled={isPending}
              />
              {errors.price && <p className="text-sm text-destructive">{errors.price}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              {t('products.discountedPrice') || 'Rabat (snižena cena)'}
              <span className="ml-1 text-xs text-muted-foreground font-normal">— ostavi prazno za bez rabata</span>
            </Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={formData.discountedPrice}
              onChange={e => handleChange('discountedPrice', e.target.value)}
              className={errors.discountedPrice ? 'border-destructive' : ''}
              disabled={isPending}
              placeholder={formData.price ? `manje od ${formData.price}` : ''}
            />
            {errors.discountedPrice && (
              <p className="text-sm text-destructive">{errors.discountedPrice}</p>
            )}
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <Label>{t('products.image')}</Label>
            <div className="flex items-start gap-4">
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-20 h-20 rounded-lg object-cover border"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    disabled={isPending}
                    className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center hover:bg-destructive/80"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-20 h-20 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                  <ImageOff className="w-6 h-6 text-muted-foreground/50" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isPending}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isPending}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? t('products.uploading') : t('products.uploadImage')}
                </Button>
                <p className="text-xs text-muted-foreground">JPEG, PNG, max 5MB</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? (uploading ? t('products.uploading') : t('common.saving')) : t('common.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
